/**
 * Turns a decoupled_toolbox error body into a user-comprehensible message.
 *
 * On a 422, decoupled_toolbox returns a generic, untranslated
 * `status.message` ("Validation failed.") plus the useful part in a per-field
 * map. The Orbit save controller enriches that into:
 *   - status.message : a label-prefixed, translated summary;
 *   - errors         : { <apiKey>: <constraint message> }        (raw, kept);
 *   - errors_labeled : { <apiKey>: { label, message } }          (added).
 *
 * This helper reads whichever shape is present (so it degrades gracefully if
 * the backend enrichment is absent) and produces a single readable message plus
 * a normalized per-field map the caller can use to mark each field.
 */

// Umbrella messages that carry no information for the user — skipped in favour
// of the per-field detail or an HTTP-based fallback.
const GENERIC_MESSAGES = new Set(['validation failed.', 'validation failed']);

/**
 * Normalize a decoupled error body to `{ <apiKey>: { label, message } }`,
 * or `null` when the body carries no per-field errors.
 *
 * @param {any} body The parsed JSON response body.
 * @returns {Object<string, {label: string, message: string}>|null}
 */
export function normalizeFieldErrors(body) {
  const pick = (source) => {
    if (!source || typeof source !== 'object' || Array.isArray(source)) return null;
    const out = {};
    for (const [key, val] of Object.entries(source)) {
      if (typeof val === 'string' && val.trim()) {
        out[key] = { label: '', message: val.trim() };
      }
      else if (val && typeof val === 'object' && typeof val.message === 'string' && val.message.trim()) {
        out[key] = {
          label: typeof val.label === 'string' ? val.label : '',
          message: val.message.trim(),
        };
      }
    }
    return Object.keys(out).length ? out : null;
  };

  // Prefer the labeled shape (richer), fall back to the raw errors map.
  return pick(body?.errors_labeled) || pick(body?.errors);
}

/**
 * Build a human message (and the per-field map) from a decoupled error body.
 *
 * @param {any} body The parsed JSON response body (may be null/undefined).
 * @param {number} [httpStatus] The HTTP status, used only for the last-resort
 *   fallback message.
 * @returns {{message: string, fieldErrors: (Object<string, {label: string, message: string}>|null)}}
 */
export function formatDecoupledError(body, httpStatus) {
  const fieldErrors = normalizeFieldErrors(body);

  if (fieldErrors) {
    const entries = Object.values(fieldErrors);
    const line = (e) => `${e.label ? e.label + ' : ' : ''}${e.message}`;
    if (entries.length === 1) {
      return { message: line(entries[0]), fieldErrors };
    }
    const list = entries.map((e) => `• ${line(e)}`).join('\n');
    return { message: `Certains champs sont invalides :\n${list}`, fieldErrors };
  }

  const statusMessage = typeof body?.status?.message === 'string' ? body.status.message.trim() : '';
  if (statusMessage && !GENERIC_MESSAGES.has(statusMessage.toLowerCase())) {
    return { message: statusMessage, fieldErrors: null };
  }

  const suffix = Number.isFinite(httpStatus) ? ` (HTTP ${httpStatus})` : '';
  return { message: `La sauvegarde a échoué${suffix}.`, fieldErrors: null };
}
